import { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { jwtDecode } from 'jwt-decode';
import { setUserContext } from '../services/sentry';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

interface CustomClaims {
  adminInCompanies: { [key: string]: boolean };
  isSuperAdmin: boolean;
}

interface UserDetails {
  name?: string;
  email?: string;
}

// Add interface for our JWT token structure
interface CustomJwtPayload {
  company_roles?: { [key: string]: boolean };
  is_super_admin?: boolean;
  app_metadata?: {
    isSuperAdmin?: boolean;
  };
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  customClaims: CustomClaims | null;
  userDetails: UserDetails | null;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  customClaims: null,
  userDetails: null,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [customClaims, setCustomClaims] = useState<CustomClaims | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);

  // Helper function to extract claims from token
  const extractCustomClaims = (accessToken: string): CustomClaims => {
    const decodedToken = jwtDecode<CustomJwtPayload>(accessToken);
    return {
      adminInCompanies: decodedToken.company_roles ?? {},
      isSuperAdmin: decodedToken.is_super_admin ?? false,
    };
  };

  useEffect(() => {
    // Get initial session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (session?.user) {
          const claims = extractCustomClaims(session.access_token);
          setCustomClaims(claims);
          setUserContext(session.user, session.access_token);
          setUserDetails({
            name: `${session.user.user_metadata.first_name} ${session.user.user_metadata.last_name}`,
            email: session.user.email,
          });
        } else {
          setUserContext(null);
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        //
        console.error('Error getting session:', error);

        alert(t('auth.session_expired'));
        supabase.auth.signOut().then(() => {
          window.location.href = '/login';
        });
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        const claims = extractCustomClaims(session.access_token);
        setCustomClaims(claims);
        setUserContext(session.user, session.access_token);
      } else {
        setUserContext(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [t]);

  return (
    <AuthContext.Provider value={{ session, user, loading, customClaims, userDetails }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
